// ---- Special combat moves (plan 14 + AoE shapes + ranged bow specials) ------
// The moves registry: content/moves/*.json hydrated by src/content/load.ts.
// A move rides the equipped weapon (weapon.moves declares support) and fills
// ability slots 1–4 (keys 1–4). Primary attack is separate (F / light weapon).
// DOM- and THREE-free — the server (attack/fire handlers), the client (combat.ts
// slots), and the studio arena all import this same list.

import { loadedMoves } from "./content/load.ts";

import type { CombatEffect } from "./sim/status.ts";
import {
  type AoeShape,
  DEFAULT_FALLOFF_MIN,
} from "./sim/aoe.ts";

/** Content shape on a move doc (weapon-relative mults, not yet world units). */
export type MoveShape =
  | { kind: "sector"; arcDeg: number; rangeMult: number }
  | { kind: "line"; lengthMult: number; width: number };

export type MoveSpec = {
  id: string;
  name: string;
  /** Action-bar slot icon (emoji). */
  icon: string;
  /** Which attack clip animates it (a content/anim/clips.json style). */
  clip: "punch" | "swing" | "chop" | "thrust" | "draw";
  /** × the weapon's damage. */
  damageMult: number;
  /** This slot's OWN cooldown, seconds — independent of the light attack's. */
  cooldown: number;
  /** Absolute stamina cost (not a multiplier). */
  staminaCost: number;
  /** melee (default) = shape cleave; ranged = focus-target projectile. */
  delivery?: "melee" | "ranged";
  /** instant (default) = press once; charge = hold key, full only on release. */
  cast?: "instant" | "charge";
  /** Seconds to full charge when cast is charge. */
  chargeTime?: number;
  /** Hit volume — required for melee delivery. */
  shape?: MoveShape;
  /** Damage fraction at max range (default DEFAULT_FALLOFF_MIN). Effects full. */
  falloffMin?: number;
  /** Skill box that must be unlocked to use it (plan-09 gate, server-checked). */
  requiresBox?: string;
  /** Crowd-control applied on each landed hit (snare / stun / slow / knockback). */
  effects?: CombatEffect[];
};

export const MOVES: MoveSpec[] = loadedMoves as MoveSpec[];

const byId = new Map(MOVES.map((m) => [m.id, m]));

export function moveById(id: string): MoveSpec | undefined {
  return byId.get(id);
}

/** How many ability action-bar slots exist (keys 1–4). With the primary attack
 *  (F) this is a fixed 5-slot combat strip. */
export const MOVE_SLOTS = 4;

/** Resolve a weapon's `moves` id list to specs — unknown ids dropped, capped at
 *  the slot count. THE one copy of the slot-fill rule (the game's action bar
 *  and the studio arena both fill from it). */
export function weaponMoveSpecs(w: { moves?: string[] } | null | undefined): MoveSpec[] {
  const out: MoveSpec[] = [];
  for (const id of w?.moves ?? []) {
    const m = byId.get(id);
    if (m) out.push(m);
    if (out.length === MOVE_SLOTS) break;
  }
  return out;
}

export function moveDelivery(m: MoveSpec): "melee" | "ranged" {
  return m.delivery ?? "melee";
}

export function moveCast(m: MoveSpec): "instant" | "charge" {
  return m.cast ?? "instant";
}

/** × weapon attackRange for candidate / feel reach (sector rangeMult or line lengthMult). */
export function moveRangeMult(m: MoveSpec): number {
  if (!m.shape) return 1;
  return m.shape.kind === "sector" ? m.shape.rangeMult : m.shape.lengthMult;
}

/** Resolve a move's content shape against a weapon's attackRange into world units.
 *  Returns null when the move has no shape (ranged specials) — callers must not
 *  invent dummy geometry. */
export function resolveMoveShape(m: MoveSpec, weaponRange: number): AoeShape | null {
  if (!m.shape) return null;
  if (m.shape.kind === "sector") {
    return {
      kind: "sector",
      arcDeg: m.shape.arcDeg,
      range: weaponRange * m.shape.rangeMult,
    };
  }
  return {
    kind: "line",
    length: weaponRange * m.shape.lengthMult,
    width: m.shape.width,
  };
}

/** Effective falloff floor for a move (content or default). */
export function moveFalloffMin(m: MoveSpec): number {
  return m.falloffMin ?? DEFAULT_FALLOFF_MIN;
}
